<script lang="ts">
  /// メンバーの Auth0 ロール変更モーダル (ACC-7)。メンバー画面と組織詳細画面で共有する。
  ///
  /// FSD: features はユースケース UI + フロー判断を持つ。付与可能ロールの算出 (権限昇格ガードの
  /// UI ミラー) と changeMemberRole の呼び出しをここに閉じ込め、ページは「対象 member と org を
  /// 渡して結果を受け取る」だけにする。org スコープ強制と付与上限の真実は backend。
  import { onMount } from 'svelte';
  import { changeMemberRole, getMemberRoles } from '@shared/api/members';
  import { listOrgRoles } from '@shared/api/admin';
  import type { AdminRole } from '@shared/types/admin';
  import type { Member } from '@shared/types/member';
  import { sessionStore } from '@features/auth/session/model/store.svelte';
  import RoleMultiSelect from '@shared/ui/RoleMultiSelect.svelte';

  interface Props {
    /// 変更対象。親が `{#if member}` で開閉する (非 null のときだけマウントする想定)。
    member: Member;
    /// ロール付与先の Auth0 organization。未解決なら送信させない。
    orgId: string | null;
    onClose: () => void;
    /// 変更成功時。親は一覧を refresh する。
    onChanged: () => void | Promise<void>;
  }

  const { member, orgId, onClose, onChanged }: Props = $props();

  const actorRole = $derived(sessionStore.user?.role ?? null);

  // 複数ロール選択。マウント時に現在の全 role_id を初期選択し、1 つ足しても既存が消えないよう
  // 温存する (set_member_roles は差分置換のため、初期選択しないと付与済みロールが消える)。
  let roleIds = $state<string[]>([]);
  let roles = $state<AdminRole[]>([]);
  let loadError = $state<string | null>(null);
  let ready = $state(false);

  let submitting = $state(false);
  let formError = $state<string | null>(null);

  onMount(async () => {
    if (!orgId) {
      loadError = '組織が解決できていません。';
      ready = true;
      return;
    }
    try {
      // カタログと現在ロールを並行ロード。現在ロールの全 role_id を初期選択にする。
      const [catalogue, current] = await Promise.all([
        listOrgRoles(orgId),
        getMemberRoles(member.id, orgId),
      ]);
      roles = catalogue;
      roleIds = current.map((r) => r.id);
    } catch (err) {
      loadError = err instanceof Error ? err.message : String(err);
    } finally {
      ready = true;
    }
  });

  async function handleSubmit(ev: SubmitEvent) {
    ev.preventDefault();
    if (submitting) return;
    if (!orgId) {
      formError = '組織が解決できていません。';
      return;
    }
    if (roleIds.length === 0) {
      formError = '権限ロールを最低1つ選んでください。';
      return;
    }
    submitting = true;
    formError = null;
    try {
      await changeMemberRole(member.id, { role_ids: roleIds, organization_id: orgId });
      await onChanged();
      onClose();
    } catch (err) {
      formError = err instanceof Error ? err.message : String(err);
    } finally {
      submitting = false;
    }
  }

  function handleClose() {
    if (submitting) return;
    onClose();
  }
</script>

<div class="modal-backdrop">
  <button type="button" class="modal-dismiss" aria-label="閉じる" onclick={handleClose}></button>
  <div class="modal-card" role="dialog" aria-modal="true">
    <h2>ロール変更</h2>
    <p class="muted">
      {member.display_name} の Auth0 ロールを変更します。Auth0 組織のロール付与と CRM
      側の履歴記録を同時に行います。
    </p>
    <form onsubmit={handleSubmit}>
      <div class="field">
        <span class="field-label">ロール <em>*</em></span>
        {#if !ready}
          <small class="hint">ロールを読込中…</small>
        {:else if loadError}
          <small class="error">ロールの取得に失敗しました: {loadError}</small>
        {:else}
          <RoleMultiSelect bind:value={roleIds} {roles} {actorRole} />
          <small class="hint">現在の全ロールが初期選択されます。権限ロールを最低1つ残してください。</small>
        {/if}
      </div>
      {#if formError}
        <div class="error">{formError}</div>
      {/if}
      <div class="modal-actions">
        <button type="button" class="btn" disabled={submitting} onclick={handleClose}>
          キャンセル
        </button>
        <button type="submit" class="btn btn-primary" disabled={submitting || !ready || !!loadError}>
          {submitting ? '変更中…' : '変更する'}
        </button>
      </div>
    </form>
  </div>
</div>

<style>
  .modal-backdrop {
    position: fixed;
    inset: 0;
    display: grid;
    place-items: center;
    z-index: 100;
  }
  .modal-dismiss {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    border: 0;
    padding: 0;
    cursor: pointer;
  }
  .modal-card {
    position: relative;
    background: var(--bg, #fff);
    padding: 20px;
    border-radius: 8px;
    min-width: 360px;
    max-width: 90vw;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  }
  .modal-card h2 {
    margin: 0 0 12px;
  }
  .modal-card p.muted {
    margin: 0 0 12px;
  }
  .field {
    display: block;
    margin-bottom: 12px;
  }
  .field-label {
    display: block;
    font-size: 12px;
    color: var(--muted);
    margin-bottom: 4px;
  }
  .hint {
    display: block;
    color: var(--muted);
    font-size: 11px;
    margin-top: 4px;
  }
  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 8px;
  }
  .error {
    color: var(--danger, #c83232);
    background: rgba(200, 50, 50, 0.06);
    padding: 8px;
    border-radius: 4px;
    margin: 8px 0;
  }
</style>
